import { summaryText } from '@/lib/eventSummary';
/**
 * Vapelarm Alarm System
 * 
 * Handles sound alerts, vibration, browser notifications, and push subscription.
 * Designed to be LOUD and IMPOSSIBLE TO MISS on mobile and desktop.
 */

const ALARM_STORAGE_KEY = 'va_sound';

// ─── Audio Context Singleton ───
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (_audioCtx.state === 'suspended') _audioCtx.resume();
  return _audioCtx;
}

// ─── Sound Enabled Check ───
export function isSoundEnabled() {
  return localStorage.getItem(ALARM_STORAGE_KEY) !== 'off';
}
export function toggleSound() {
  const next = isSoundEnabled() ? 'off' : 'on';
  localStorage.setItem(ALARM_STORAGE_KEY, next);
  return next === 'on';
}

// ─── Vibration ───
function vibrate(pattern) {
  try {
    if ('vibrate' in navigator) navigator.vibrate(pattern);
  } catch {}
}

// ─── Play a single tone ───
function playTone(freq, duration, volume, type = 'square', delay = 0) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const start = ctx.currentTime + delay;
    gain.gain.setValueAtTime(volume, start);
    gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
    osc.start(start);
    osc.stop(start + duration);
  } catch {}
}

// ─── ALARM: Vape detected (HIGH urgency) ───
export function playVapeAlarm() {
  if (!isSoundEnabled()) return;
  // Aggressive three-tone siren, repeated 3 times
  // Total: ~3 seconds of LOUD alert
  const vol = 0.7;
  for (let rep = 0; rep < 3; rep++) {
    const offset = rep * 1.0;
    playTone(880, 0.15, vol, 'square', offset);
    playTone(1320, 0.15, vol, 'square', offset + 0.18);
    playTone(1760, 0.20, vol, 'sawtooth', offset + 0.38);
    playTone(880, 0.10, vol, 'square', offset + 0.62);
    playTone(1760, 0.15, vol, 'sawtooth', offset + 0.75);
  }
  // Heavy vibration: long bursts
  vibrate([300, 100, 300, 100, 300, 100, 500, 200, 500]);
}

// ─── ALARM: Suspect activity (MEDIUM urgency) ───
export function playSuspectAlarm() {
  if (!isSoundEnabled()) return;
  const vol = 0.5;
  for (let rep = 0; rep < 2; rep++) {
    const offset = rep * 0.8;
    playTone(660, 0.2, vol, 'square', offset);
    playTone(990, 0.2, vol, 'square', offset + 0.25);
  }
  vibrate([200, 100, 200, 100, 200]);
}

// ─── ALARM: Device offline (LOW urgency) ───
export function playOfflineAlert() {
  if (!isSoundEnabled()) return;
  playTone(440, 0.4, 0.3, 'sine');
  vibrate([150, 100, 150]);
}

// ─── Trigger alarm based on event ───
export function triggerAlarm(event) {
  if (!event) return;
  const type = event.event_type;
  const severity = event.severity;

  if (type === 'vape' || severity === 'high') {
    playVapeAlarm();
  } else if (type === 'suspect' || severity === 'medium') {
    playSuspectAlarm();
  } else if (type === 'offline') {
    playOfflineAlert();
  }
}

// ─── Browser Notification ───
export async function requestNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return await Notification.requestPermission();
}

export function showBrowserNotification(event) {
  if (!('Notification' in window) || Notification.permission !== 'granted' || !event) return;

  const isVape = event.event_type === 'vape';
  const sevLabel = event.severity === 'high' ? 'HOG' : event.severity === 'medium' ? 'MEDEL' : '';
  const title = isVape
    ? `VAPE LARM ${sevLabel} — ${event.school_name || ''}`
    : `${(event.event_type || '').toUpperCase()} ${sevLabel}`;

  try {
    const n = new Notification(title, {
      body: `${event.room_name || 'Rum'} — ${event.school_name || ''}\n${summaryText(event)}`,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: event.id || 'vapelarm',
      requireInteraction: isVape || event.severity === 'high',
      silent: false,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {}
}

// ─── Service Worker Push Registration ───

// Check if we're in iOS standalone PWA mode
function isIOSStandalone() {
  return (
    ('standalone' in navigator && navigator.standalone) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

export function canUsePush() {
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS && !isIOSStandalone()) return false;
  return true;
}

export async function registerPushSubscription(token) {
  if (!canUsePush()) {
    console.warn('Push: not supported in this context');
    return null;
  }
  try {
    // Step 1: Request notification permission (must be first in user gesture)
    const permission = Notification.requestPermission();
    const result = permission instanceof Promise ? await permission : permission;
    if (result !== 'granted') {
      console.warn('Push: notification permission denied:', result);
      return null;
    }

    // Step 2: Ensure service worker is registered and ready
    let reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
      console.log('Push: SW not registered, registering now...');
      reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    }
    // Wait for activation
    await navigator.serviceWorker.ready;
    reg = await navigator.serviceWorker.getRegistration('/');

    if (!reg || !reg.active) {
      console.warn('Push: SW not active after registration');
      return null;
    }

    // Step 3: Fetch VAPID key
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${API_URL}/api/push/vapid-key`);
    const { publicKey } = await res.json();
    if (!publicKey) {
      console.warn('Push: no VAPID key from server');
      return null;
    }

    // Convert base64url VAPID key to Uint8Array
    const padding = '='.repeat((4 - publicKey.length % 4) % 4);
    const base64 = (publicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const applicationServerKey = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i++) applicationServerKey[i] = rawData.charCodeAt(i);

    // Step 4: Subscribe to push (always re-subscribe to use current VAPID key)
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      await sub.unsubscribe();
    }
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    // Step 5: Send subscription to backend
    if (sub && token) {
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      console.log('Push: subscription registered successfully');
    }

    return sub;
  } catch (e) {
    console.warn('Push subscription failed:', e);
    return null;
  }
}

// ─── Initialize alarm system: request permissions + resume AudioContext on interaction ───
export function initAlarmSystem() {
  // Request notification permission
  requestNotificationPermission();

  // Resume AudioContext on first user interaction (required by browsers)
  const resume = () => {
    try {
      getAudioCtx();
    } catch {}
    document.removeEventListener('click', resume);
    document.removeEventListener('touchstart', resume);
  };
  document.addEventListener('click', resume, { once: true });
  document.addEventListener('touchstart', resume, { once: true });
}
